import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  ArrowDownUp,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Search,
  X,
  Clock,
} from 'lucide-react';
import { LoadingSpinner } from '../onboarding/LoadingIndicator';
import { QueryRow } from '../../types/viewReports';
import { RootState, useAppDispatch, useAppSelector } from '../../redux/store';
import {
  fetchReportQueries,
  loadMoreQueries,
  setSortBy,
  setSortDirection,
  setPerformanceFilter,
} from '../../redux/slice/viewReportSlice';

export interface QueryTableProps {
  reportId: string;
  projectId: number;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Helper to format time value
function formatTime(ms: number | undefined): string {
  if (ms === undefined || ms === null) return '-';
  if (ms < 1000) {
    return `${ms.toFixed(2)} ms`;
  }
  return `${(ms / 1000).toFixed(2)} s`;
}

// Helper to get display time (avg time)
function getDisplayTime(row: QueryRow, dataSize: '1x' | '10x'): string {
  if (dataSize === '1x') {
    return row.time1x ? formatTime(row.time1x.avg) : '-';
  }
  return row.time10x ? formatTime(row.time10x.avg) : '-';
}

// Helper to get time values for expanded view
function getTimeDetails(
  row: QueryRow,
  dataSize: '1x' | '10x'
): { min: string; max: string; avg: string; stdDev?: string } {
  const timeData = dataSize === '1x' ? row.time1x : row.time10x;

  if (timeData) {
    return {
      min: formatTime(timeData.min),
      max: formatTime(timeData.max),
      avg: formatTime(timeData.avg),
      stdDev: timeData.stdDev?.toFixed(2),
    };
  }

  return { min: '-', max: '-', avg: '-' };
}

// Helper to check if time data is available
function hasTimeData(row: QueryRow, dataSize: '1x' | '10x'): boolean {
  return dataSize === '1x' ? !!row.time1x : !!row.time10x;
}

export function QueryTable({ reportId, projectId }: QueryTableProps) {
  const dispatch = useAppDispatch();
  const runId = useAppSelector(
    (state: RootState) => state.userProject.currentRunId
  );

  // Get report state from Redux
  const reportState = useAppSelector(
    (state: RootState) => state.viewReports.reports[reportId]
  );

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [fullScreenQuery, setFullScreenQuery] = useState<QueryRow | null>(null);

  const loaderRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Debounce search input (500ms)
  const debouncedSearchQuery = useDebounce(localSearchQuery, 500);

  // Get values from Redux state
  const queries = reportState?.queries || [];
  const isLoading = reportState?.isLoading || false;
  const isLoadingMore = reportState?.isLoadingMore || false;
  const hasMore = reportState?.hasMore || false;
  const currentPage = reportState?.currentPage || 0;
  const querySummary = reportState?.querySummary || {
    green: 0,
    yellow: 0,
    red: 0,
    total: 0,
  };
  const sortBy = reportState?.sortBy || null;
  const sortDirection = reportState?.sortDirection || 'asc';
  const performanceFilter = reportState?.performanceFilter || 'all';
  const searchText = reportState?.searchText || '';

  // Calculate total count based on current filter
  const getTotalCount = (): number => {
    let count: number;
    
    if (searchText.trim()) {
      count = reportState?.totalCount || 0;
    } else {
      switch (performanceFilter) {
        case 'green':
          count = querySummary.green;
          break;
        case 'yellow':
          count = querySummary.yellow;
          break;
        case 'red':
          count = querySummary.red;
          break;
        case 'all':
        default:
          count = querySummary.total;
      }
    }
    
    return Math.max(count, queries.length);
  };
  const totalCount = getTotalCount();

  // Load or reload queries whenever the scoped run (or project) changes — not only
  // when the list is empty, otherwise switching runs keeps the previous run's rows.
  useEffect(() => {
    if (!reportState || runId == null || projectId <= 0) return;
    dispatch(
      fetchReportQueries(projectId, runId, reportId, {
        performanceFilter,
      })
    );
    // Intentionally omit performanceFilter: filter changes use handleSort / filter handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, reportId, projectId, runId]);

  useEffect(() => {
    setExpandedRows(new Set());
    setLocalSearchQuery('');
    setFullScreenQuery(null);
  }, [runId]);

  // Refetch when debounced search changes
  useEffect(() => {
    if (reportState && debouncedSearchQuery !== reportState.searchText) {
      dispatch(
        fetchReportQueries(projectId, runId, reportId, {
          searchQuery: debouncedSearchQuery || undefined,
          sortBy: sortBy || undefined,
          sortDirection,
          performanceFilter,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery]);

  // Handle sort change
  const handleSort = (scale: '1x' | '10x' | 'Ratio') => {
    let newSortBy: '1x' | '10x' | 'Ratio' | null = scale;
    let newSortDirection: 'asc' | 'desc' = 'asc';

    if (sortBy === scale) {
      if (sortDirection === 'asc') {
        newSortDirection = 'desc';
      } else {
        newSortBy = null;
        newSortDirection = 'asc';
      }
    }

    dispatch(setSortBy({ reportId, sortBy: newSortBy }));
    dispatch(setSortDirection({ reportId, sortDirection: newSortDirection }));

    dispatch(
      fetchReportQueries(projectId, runId, reportId, {
        searchQuery: localSearchQuery || undefined,
        sortBy: newSortBy || undefined,
        sortDirection: newSortDirection,
        performanceFilter,
      })
    );
  };

  // Handle performance filter change (called from parent via props)
  const handlePerformanceFilterChange = useCallback(
    (filter: 'all' | 'green' | 'yellow' | 'red') => {
      dispatch(setPerformanceFilter({ reportId, filter }));

      dispatch(
        fetchReportQueries(projectId, runId, reportId, {
          searchQuery: localSearchQuery || undefined,
          sortBy: sortBy || undefined,
          sortDirection,
          performanceFilter: filter,
        })
      );
    },
    [dispatch, reportId, projectId, runId, localSearchQuery, sortBy, sortDirection]
  );

  // Expose filter change handler to parent
  useEffect(() => {
    // Store reference for parent component
    (window as any)[`filterHandler_${reportId}`] =
      handlePerformanceFilterChange;
    return () => {
      delete (window as any)[`filterHandler_${reportId}`];
    };
  }, [handlePerformanceFilterChange, reportId]);

  // Infinite scroll - load more
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return;

    dispatch(
        loadMoreQueries(projectId, runId, reportId, currentPage, {
        searchQuery: localSearchQuery || undefined,
        sortBy: sortBy || undefined,
        sortDirection,
        performanceFilter,
      })
    );
  }, [
    isLoadingMore,
    hasMore,
    isLoading,
    projectId,
    runId,
    reportId,
    currentPage,
    localSearchQuery,
    sortBy,
    sortDirection,
    performanceFilter,
    dispatch,
  ]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore
        ) {
          loadMore();
        }
      },
      {
        threshold: 0.1,
        root: scrollContainerRef.current,
      }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore, hasMore, isLoading, isLoadingMore]);

  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Calculate ratio between 1x and 10x times
  const getRatio = (row: QueryRow): number | null => {
    if (!row.time1x?.avg || !row.time10x?.avg || row.time1x.avg === 0)
      return null;
    return row.time10x.avg / row.time1x.avg;
  };

  const getTimeColorClass = (row: QueryRow, isFor10x: boolean): string => {
    if (!isFor10x) return 'text-gray-900';
    const ratio = getRatio(row);
    if (ratio === null) return 'text-gray-900';
    if (ratio <= 2) return 'text-green-600';
    if (ratio <= 5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCardColorClass = (row: QueryRow, isFor10x: boolean): string => {
    if (!isFor10x) return 'bg-white border-gray-200';
    const ratio = getRatio(row);
    if (ratio === null) return 'bg-white border-gray-200';
    if (ratio <= 2) return 'bg-green-50 border-green-200';
    if (ratio <= 5) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getRatioBadge = (
    row: QueryRow
  ): { ratio: string; colorClass: string } | null => {
    const ratio = getRatio(row);
    if (ratio === null) return null;
    const ratioStr = ratio.toFixed(1) + '×';
    if (ratio <= 2)
      return { ratio: ratioStr, colorClass: 'bg-green-100 text-green-700' };
    if (ratio <= 5)
      return { ratio: ratioStr, colorClass: 'bg-yellow-100 text-yellow-700' };
    return { ratio: ratioStr, colorClass: 'bg-red-100 text-red-700' };
  };

  const flattenQuery = (query: string): string => {
    return query.replace(/\s+/g, ' ').trim();
  };

  const truncateQuery = (query: string, maxLength = 80): string => {
    const flattened = flattenQuery(query);
    if (flattened.length <= maxLength) return flattened;
    return flattened.substring(0, maxLength) + '...';
  };

  return (
    <div className="space-y-3">
      {/* Search and Sort Controls */}
      <div className="space-y-2 pt-3">
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tables (e.g., users AND orders)"
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {localSearchQuery && (
              <button
                type="button"
                onClick={() => {
                  setLocalSearchQuery('');
                  dispatch(
                    fetchReportQueries(projectId, runId, reportId, {
                      searchQuery: undefined,
                      sortBy: sortBy || undefined,
                      sortDirection,
                      performanceFilter,
                    })
                  );
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 mr-1">Sort:</span>
            {([
              { value: '1x', label: '1x' },
              { value: '10x', label: '10x' },
              { value: 'Ratio', label: 'Ratio 10x/1x' },
            ] as const).map((sortOption) => (
              <button
                key={sortOption.value}
                onClick={() => handleSort(sortOption.value)}
                disabled={isLoading}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md flex items-center gap-1 transition-colors ${
                  sortBy === sortOption.value
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {sortOption.label}
                {sortBy === sortOption.value ? (
                  sortDirection === 'asc' ? (
                    <ArrowUp className="w-3 h-3" />
                  ) : (
                    <ArrowDown className="w-3 h-3" />
                  )
                ) : (
                  <ArrowDownUp className="w-3 h-3 opacity-50" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Help text */}
        <p className="text-xs text-gray-400">
          Use AND/OR for multiple tables: "users AND orders" or "products OR
          inventory"
        </p>

        {/* Sort status */}
        {sortBy && (
          <p className="text-xs text-gray-500">
            Sorted by {sortBy === 'Ratio' ? '10x/1x ratio' : `${sortBy} time`} (
            {sortDirection === 'asc' ? 'low to high' : 'high to low'})
            <button
              onClick={() => handleSort(sortBy)}
              className="ml-2 text-blue-600 hover:text-blue-800"
              disabled={isLoading}
            >
              Clear sort
            </button>
          </p>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center gap-2.5 py-12 text-gray-600">
          <LoadingSpinner size="xl" className="text-blue-600" />
          <span className="text-sm">Loading queries...</span>
        </div>
      )}

      {/* Query list */}
      {!isLoading && (
        <div
          ref={scrollContainerRef}
          className="max-h-[500px] overflow-y-auto border border-solid border-gray-200 rounded-lg"
        >
          <div className="sticky top-0 bg-gray-50 px-3 py-1.5 border-b border-gray-200 z-10">
            <span className="text-xs text-gray-500">
              Showing {queries.length} of {totalCount} queries
            </span>
          </div>

          <div className="p-3 space-y-2">
            {queries.map((row) => {
              const isExpanded = expandedRows.has(row.id);
              const time1Display = getDisplayTime(row, '1x');
              const time10Display = getDisplayTime(row, '10x');
              const time1Details = getTimeDetails(row, '1x');
              const time10Details = getTimeDetails(row, '10x');
              const ratioBadge = getRatioBadge(row);

              return (
                <div
                  key={row.id}
                  className="border border-solid border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors bg-white"
                >
                  <button
                    onClick={() => toggleExpand(row.id)}
                    className="w-full px-4 py-3 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left"
                  >
                    {/* Expand/Collapse Icon */}
                    <div className="text-gray-400">
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </div>

                    {/* Query Text */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 font-mono truncate">
                        {truncateQuery(row.query)}
                      </p>
                    </div>

                    {/* Execution Times - Only show when collapsed */}
                    {!isExpanded && (
                      <div className="flex items-center gap-3 text-xs shrink-0">
                        {hasTimeData(row, '1x') && (
                          <>
                            <span className="text-gray-400">1x:</span>
                            <span className="font-medium text-gray-900 w-20">
                              {time1Display}
                            </span>
                          </>
                        )}
                        {hasTimeData(row, '10x') && (
                          <>
                            <span className="text-gray-400">10x:</span>
                            <span
                              className={`font-medium w-20 ${getTimeColorClass(
                                row,
                                true
                              )}`}
                            >
                              {time10Display}
                            </span>
                          </>
                        )}
                        {ratioBadge && (
                          <>
                            <span className="text-gray-400">10x/1x: </span>
                            <span
                              className={`text-xs font-medium px-2 py-1 rounded ${ratioBadge.colorClass}`}
                            >
                              {ratioBadge.ratio}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                      {/* Query Code Block */}
                      <div className="mt-3 mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 font-medium">
                            Query
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFullScreenQuery(row);
                            }}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                          >
                            <Maximize2 className="w-3 h-3" />
                            View full query
                          </button>
                        </div>
                        <div className="bg-gray-900 rounded-lg p-3 max-h-[200px] overflow-auto">
                          <pre className="text-xs text-gray-100 font-mono whitespace-pre-wrap break-all">
                            {row.query}
                          </pre>
                        </div>
                      </div>

                      {/* Execution Time Cards */}
                      <div className="grid grid-cols-2 gap-3">
                        {/* 1x Card */}
                        {hasTimeData(row, '1x') && (
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="text-xs text-gray-500">
                                Baseline Data (1x)
                              </span>
                            </div>
                            <p className="text-2xl font-bold text-gray-900 mb-2">
                              {time1Details.avg}
                            </p>
                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                              <div>
                                <span className="text-xs text-gray-400">
                                  Min
                                </span>
                                <p className="text-sm font-medium text-gray-700">
                                  {time1Details.min}
                                </p>
                              </div>
                              <div>
                                <span className="text-xs text-gray-400">
                                  Max
                                </span>
                                <p className="text-sm font-medium text-gray-700">
                                  {time1Details.max}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* 10x Card */}
                        {hasTimeData(row, '10x') && (
                          <div
                            className={`border rounded-lg p-4 ${getCardColorClass(
                              row,
                              true
                            )}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span className="text-xs text-gray-500">
                                  10x Data Scale
                                </span>
                              </div>
                              {ratioBadge && (
                                <span
                                  className={`text-xs font-medium px-1.5 py-0.5 rounded ${ratioBadge.colorClass}`}
                                >
                                  {ratioBadge.ratio}
                                </span>
                              )}
                            </div>
                            <p
                              className={`text-2xl font-bold mb-2 ${getTimeColorClass(
                                row,
                                true
                              )}`}
                            >
                              {time10Details.avg}
                            </p>
                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                              <div>
                                <span className="text-xs text-gray-400">
                                  Min
                                </span>
                                <p className="text-sm font-medium text-gray-700">
                                  {time10Details.min}
                                </p>
                              </div>
                              <div>
                                <span className="text-xs text-gray-400">
                                  Max
                                </span>
                                <p className="text-sm font-medium text-gray-700">
                                  {time10Details.max}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Show message if only one data size is available */}
                        {!hasTimeData(row, '1x') && hasTimeData(row, '10x') && (
                          <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 flex items-center justify-center">
                            <p className="text-xs text-gray-500 text-center">
                              1x baseline data not available
                            </p>
                          </div>
                        )}
                        {hasTimeData(row, '1x') && !hasTimeData(row, '10x') && (
                          <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 flex items-center justify-center">
                            <p className="text-xs text-gray-500 text-center">
                              10x scale data not available
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Loader for infinite scroll */}
            <div ref={loaderRef} className="py-2">
              {isLoadingMore && (
                <div className="flex justify-center py-16">
                  <LoadingSpinner size="lg" className="text-blue-600" />
                </div>
              )}
              {!hasMore && queries.length > 0 && (
                <p className="text-center text-xs text-gray-400">
                  All {totalCount} queries loaded
                </p>
              )}
              {queries.length === 0 && !isLoading && (
                <p className="text-center text-sm text-gray-500 py-4">
                  No queries match your search
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Modal */}
      {fullScreenQuery && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setFullScreenQuery(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Query Details</h3>
              <button
                onClick={() => setFullScreenQuery(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
              <div className="bg-gray-900 rounded-lg p-4">
                <pre className="text-sm text-gray-100 font-mono whitespace-pre-wrap break-all">
                  {fullScreenQuery.query}
                </pre>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 border-t border-gray-200 bg-gray-50">
              {hasTimeData(fullScreenQuery, '1x') && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">1x:</span>
                  <span className="font-medium text-gray-900">
                    {getDisplayTime(fullScreenQuery, '1x')}
                  </span>
                </div>
              )}
              {hasTimeData(fullScreenQuery, '10x') && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">10x:</span>
                  <span
                    className={`font-medium ${getTimeColorClass(
                      fullScreenQuery,
                      true
                    )}`}
                  >
                    {getDisplayTime(fullScreenQuery, '10x')}
                  </span>
                  {getRatioBadge(fullScreenQuery) && (
                    <span
                      className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                        getRatioBadge(fullScreenQuery)!.colorClass
                      }`}
                    >
                      {getRatioBadge(fullScreenQuery)!.ratio}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QueryTable;
